import { useTheme } from "@mui/material";
import { Hold20Px } from "@qubit/autoparts";
import { parse, stringify } from "qs";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { setSelectedSummaries } from "~/features/inventory/inventory.slice";
import { useConfigurePortsWithRetry } from "~/hooks/useConfigurePortsWithRetry";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import {
  selectThisWorkstation,
  selectWorkstationHasInventoryRole
} from "~/redux/selectors/workstationsSelectors";

import { MainButton } from "./MainButton";
import { PreparingWorkstationModal } from "./PreparingWorkstationModal";

export function InventoryHoldsButton() {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const navigate = useFcNavigate();
  const dispatch = useAppDispatch();
  const { search }: { search: string } = useLocation();
  const trackedPageName = useMixpanelPageName();
  const isInventoryRoleFound = useAppSelector(
    selectWorkstationHasInventoryRole
  );

  const { configurePortsWithRetry, isConfigurePortsRetrying } =
    useConfigurePortsWithRetry({
      workstationMode: "Problem Solving"
    });

  const hasWorkstation = !!useAppSelector(selectThisWorkstation);

  const onInventoryHoldsClicked = async () => {
    if (isInventoryRoleFound || !hasWorkstation) {
      const isConfigurePortsSuccess = await configurePortsWithRetry();
      if (!isConfigurePortsSuccess) {
        return;
      }

      navigate({
        pathname: "/autostore-inventory/holds",
        search: stringify({
          ...parse(search, { ignoreQueryPrefix: true }),
          autostore: true
        })
      });
      mixpanelTrack({
        type: "Button Click",
        label: "Autostore Inventory Holds Button",
        trackedPageName
      });
      dispatch(setSelectedSummaries([]));
    }
  };

  const alert =
    (!hasWorkstation && t("no workstation selected")) ||
    (!isInventoryRoleFound && hasWorkstation && t("no inventory role found")) ||
    undefined;

  const alertKind =
    (!hasWorkstation && "workstation") ||
    (!isInventoryRoleFound && hasWorkstation && "workstation") ||
    undefined;

  return (
    <>
      <MainButton
        alert={alert}
        alertKind={alertKind}
        disabled={!isInventoryRoleFound && hasWorkstation}
        onClick={onInventoryHoldsClicked}
        variant="md"
        icon={
          <Hold20Px
            fill={palette.primary.contrastText}
            width="30px"
            height="30px"
          />
        }
      >
        {t("nav.link.inventory holds")}
      </MainButton>
      <PreparingWorkstationModal isOpen={isConfigurePortsRetrying} />
    </>
  );
}
